import { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import type { TreatmentDetail } from '../../store/treatmentsSlice'
import type { Veterinarian } from '../../store/settingsSlice'

interface TreatmentDetailHeaderProps {
  treatment: TreatmentDetail
  veterinarians: Veterinarian[]
  onUpdate: (data: Partial<TreatmentDetail>) => Promise<void>
  onCancel: () => Promise<void>
}

const TREATMENT_TYPES = ['Wellness Exam', 'Sick Visit', 'Surgery', 'Dental', 'Vaccination', 'Emergency', 'Follow-Up'] as const
const STATUSES = ['Planned', 'In Progress', 'Completed', 'Cancelled'] as const

function getStatusStyle(status: string): string {
  switch (status) {
    case 'Planned':
      return 'bg-[#f0e8d0] text-status-warning'
    case 'In Progress':
      return 'bg-[#dae8f0] text-status-info'
    case 'Completed':
      return 'bg-accent-subtle-bg text-status-success'
    case 'Cancelled':
      return 'bg-border text-text-muted'
    default:
      return 'bg-border text-text-muted'
  }
}

function getTypeStyle(): string {
  return 'bg-[#e8e6e2] text-text-secondary'
}

function toDateOnly(dateStr: string): string {
  if (!dateStr) return ''
  return dateStr.split('T')[0]
}

function formatDate(dateStr: string): string {
  if (!dateStr) return ''
  const dateOnly = toDateOnly(dateStr)
  const d = new Date(dateOnly + 'T00:00:00')
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

export function TreatmentDetailHeader({ treatment, veterinarians, onUpdate, onCancel }: TreatmentDetailHeaderProps) {
  const [editOpen, setEditOpen] = useState(false)
  const [editType, setEditType] = useState('')
  const [editStatus, setEditStatus] = useState('')
  const [editVetId, setEditVetId] = useState('')
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false)
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false)
  const [vetDropdownOpen, setVetDropdownOpen] = useState(false)
  const dateInputRef = useRef<HTMLInputElement>(null)

  const txId = `TX-${String(treatment.treatment_number).padStart(4, '0')}`
  const canCancel = treatment.status !== 'Cancelled' && treatment.status !== 'Completed'

  const openEditModal = () => {
    setEditType(treatment.treatment_type)
    setEditStatus(treatment.status)
    setEditVetId(treatment.veterinarian_id || '')
    setTypeDropdownOpen(false)
    setStatusDropdownOpen(false)
    setVetDropdownOpen(false)
    setEditOpen(true)
  }

  const handleEditSave = async () => {
    await onUpdate({
      treatment_type: editType || treatment.treatment_type,
      status: editStatus || treatment.status,
      veterinarian_id: editVetId || null,
      treatment_date: dateInputRef.current?.value || treatment.treatment_date,
    })
    setEditOpen(false)
  }

  const handleCancel = async () => {
    if (window.confirm('Are you sure you want to cancel this treatment?')) {
      await onCancel()
    }
  }

  return (
    <div data-testid="treatment-detail-header">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <h1
              className="text-[16px] font-semibold text-text-primary"
              data-testid="treatment-id"
            >
              {txId}
            </h1>
            <span
              data-testid="treatment-status-badge"
              className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${getStatusStyle(treatment.status)}`}
            >
              {treatment.status}
            </span>
            <span
              data-testid="treatment-type-badge"
              className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${getTypeStyle()}`}
            >
              {treatment.treatment_type}
            </span>
          </div>
          <div className="flex items-center gap-3 text-[13px] text-text-muted flex-wrap">
            <span>
              Patient:{' '}
              <Link
                to={`/patients/${treatment.patient_id}`}
                className="text-accent hover:text-accent-dark underline"
                data-testid="treatment-patient-link"
              >
                {treatment.patient_name}
              </Link>
            </span>
            {treatment.veterinarian_name && (
              <span data-testid="treatment-veterinarian">
                Vet: {treatment.veterinarian_name}
              </span>
            )}
            {treatment.treatment_date && (
              <span data-testid="treatment-date">
                {formatDate(treatment.treatment_date)}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="edit-treatment-button"
            onClick={openEditModal}
            className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
          >
            Edit Treatment
          </button>
          {canCancel && (
            <button
              data-testid="cancel-treatment-button"
              onClick={handleCancel}
              className="inline-flex items-center h-7 px-3 rounded-[4px] bg-status-error text-white text-[13px] font-normal hover:opacity-80 transition-colors duration-100 cursor-pointer"
            >
              Cancel Treatment
            </button>
          )}
        </div>
      </div>

      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" data-testid="edit-treatment-modal">
          <div className="bg-surface rounded-[8px] shadow-elevation-2 w-full max-w-lg max-sm:max-w-[calc(100%-24px)] p-5 max-sm:p-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-[14px] font-semibold text-text-primary mb-4">Edit Treatment</h2>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1 text-[12px] text-text-muted">
                <span>Treatment Type</span>
                <div className="relative">
                  <button
                    type="button"
                    data-testid="edit-treatment-type-select"
                    onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}
                    className="w-full h-7 px-2 text-left text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent cursor-pointer"
                  >
                    {editType || 'Select type...'}
                  </button>
                  {typeDropdownOpen && (
                    <div
                      data-testid="edit-treatment-type-dropdown"
                      className="absolute z-10 top-full left-0 mt-1 w-full border border-border rounded-[4px] bg-surface max-h-48 overflow-y-auto"
                      style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                    >
                      {TREATMENT_TYPES.map((t) => (
                        <button
                          key={t}
                          type="button"
                          data-testid={`type-option-${t}`}
                          onClick={() => {
                            setEditType(t)
                            setTypeDropdownOpen(false)
                          }}
                          className="block w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-1 text-[12px] text-text-muted">
                <span>Status</span>
                <div className="relative">
                  <button
                    type="button"
                    data-testid="edit-treatment-status-select"
                    onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                    className="w-full h-7 px-2 text-left text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent cursor-pointer"
                  >
                    {editStatus || 'Select status...'}
                  </button>
                  {statusDropdownOpen && (
                    <div
                      data-testid="edit-treatment-status-dropdown"
                      className="absolute z-10 top-full left-0 mt-1 w-full border border-border rounded-[4px] bg-surface max-h-48 overflow-y-auto"
                      style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                    >
                      {STATUSES.map((s) => (
                        <button
                          key={s}
                          type="button"
                          data-testid={`status-option-${s}`}
                          onClick={() => {
                            setEditStatus(s)
                            setStatusDropdownOpen(false)
                          }}
                          className="block w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-1 text-[12px] text-text-muted">
                <span>Assigned Veterinarian</span>
                <div className="relative">
                  <button
                    type="button"
                    data-testid="edit-treatment-vet-select"
                    onClick={() => setVetDropdownOpen(!vetDropdownOpen)}
                    className="w-full h-7 px-2 text-left text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent cursor-pointer"
                  >
                    {veterinarians.find((v) => v.id === editVetId)?.name || 'None'}
                  </button>
                  {vetDropdownOpen && (
                    <div
                      data-testid="edit-treatment-vet-dropdown"
                      className="absolute z-10 top-full left-0 mt-1 w-full border border-border rounded-[4px] bg-surface max-h-48 overflow-y-auto"
                      style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                    >
                      <button
                        type="button"
                        data-testid="vet-option-none"
                        onClick={() => {
                          setEditVetId('')
                          setVetDropdownOpen(false)
                        }}
                        className="block w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                      >
                        None
                      </button>
                      {veterinarians.map((v) => (
                        <button
                          key={v.id}
                          type="button"
                          data-testid={`vet-option-${v.id}`}
                          onClick={() => {
                            setEditVetId(v.id)
                            setVetDropdownOpen(false)
                          }}
                          className="block w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                        >
                          {v.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <label className="flex flex-col gap-1 text-[12px] text-text-muted">
                Date
                <input
                  ref={dateInputRef}
                  data-testid="edit-treatment-date-input"
                  type="date"
                  defaultValue={toDateOnly(treatment.treatment_date || '')}
                  className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                data-testid="edit-cancel-button"
                onClick={() => setEditOpen(false)}
                className="inline-flex items-center h-7 px-3 rounded-[4px] border border-border text-[13px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
              >
                Cancel
              </button>
              <button
                data-testid="edit-save-button"
                onClick={handleEditSave}
                className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
